import axios from "axios";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { getApiBaseUrl } from "../../utils/utils";

export default function MedicalAttendanceDrivers() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [msg, setMsg] = useState("");
  const [errormsg, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId: initialPartnerId } = location.state || {};

  const [selectedPartner, setSelectedPartner] = useState(initialPartnerId || "");
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);

  useEffect(() => {
    fetchPartners();
    fetchAllDrivers();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchAssignedDrivers(selectedPartner);
    } else {
      setDrivers([]);
    }
  }, [selectedPartner]);

  const fetchPartners = async () => {
    try {
      const baseApiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${baseApiUrl}/v1.0/partner/retrieve_list?page=0`
      );
      // Filter for medical partners only
      const allPartners = response.data.data || [];
      const medicalPartners = allPartners.filter(
        (partner) => partner.accountType?.toLowerCase() === "medical"
      );
      setPartners(medicalPartners);
      console.log("Fetched medical partners:", medicalPartners.length);
    } catch (err) {
      console.error("Error fetching partners:", err);
      setError("Failed to load medical partners");
    }
  };

  const fetchAllDrivers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/driver/getAllDrivers`
      );
      if (response.data.success) {
        setAllDrivers(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching drivers:", err);
    }
  };

  const fetchAssignedDrivers = async (partnerId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/api/medical-attendance/config/drivers-summary?partnerId=${partnerId}`
      );
      if (response.data.success) {
        // Map the response to the expected format
        const driverData = response.data.data?.drivers || [];
        const mappedDrivers = driverData.map(d => ({
          driverId: d.driverId,
          driverName: d.driverName,
          name: d.driverName,
          mobileNumber: d.mobileNumber || '',
          shiftName: d.todayShiftName || 'Default',
          shiftStartTime: '',
          shiftEndTime: '',
          todayStatus: d.todayStatus || 'NOT_MARKED',
          active: d.isActive !== false,
        }));
        setDrivers(mappedDrivers);
      } else {
        setDrivers([]);
      }
    } catch (err) {
      console.error("Error fetching drivers:", err);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDrivers = async () => {
    if (selectedDriverIds.length === 0) {
      setError("Please select at least one driver");
      return;
    }

    try {
      // Use the config controller to assign drivers to a partner config
      const payload = {
        partnerId: parseInt(selectedPartner),
        driverIds: selectedDriverIds,
      };
      const response = await axios.post(
        `${API_URL}shypvdriverapp/api/medical-attendance/config`,
        payload
      );
      if (response.data.success) {
        setMsg("Drivers assigned successfully");
        fetchAssignedDrivers(selectedPartner);
        setShowModal(false);
        setSelectedDriverIds([]);
      } else {
        setError(response.data.message || "Failed to assign drivers");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error assigning drivers");
    }
  };

  const handleRemoveDriver = async (driverId) => {
    if (!window.confirm("Are you sure you want to remove this driver?")) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API_URL}shypvdriverapp/api/medical-attendance/config/driver/${driverId}?partnerId=${selectedPartner}`
      );
      if (response.data.success) {
        setMsg("Driver removed successfully");
        fetchAssignedDrivers(selectedPartner);
      } else {
        setError(response.data.message || "Failed to remove driver");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error removing driver");
    }
  };

  const getPartnerName = (pid) => {
    const partner = partners.find((p) => p.pid === pid);
    return partner ? partner.pname : `Partner ${pid}`;
  };

  const getAvailableDrivers = () => {
    const assignedIds = drivers.map((d) => d.driverId);
    return allDrivers.filter((d) => !assignedIds.includes(d.driverId));
  };

  const driverOptions = getAvailableDrivers().map((d) => ({
    value: d.driverId,
    label: `${d.driverId} - ${d.name}`,
  }));

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "60px",
    },
    {
      name: "Driver ID",
      selector: (row) => row.driverId,
      sortable: true,
      width: "100px",
    },
    {
      name: "Driver Name",
      selector: (row) => row.driverName || row.name || "N/A",
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div style={{ fontWeight: 500 }}>{row.driverName || row.name || "N/A"}</div>
      ),
    },
    {
      name: "Mobile",
      selector: (row) => row.mobileNumber || "N/A",
      sortable: false,
      width: "140px",
    },
    {
      name: "Shift",
      selector: (row) => row.shiftName || "Default",
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div>
          <div>{row.shiftName || "Default"}</div>
          {row.shiftStartTime && row.shiftEndTime && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              {row.shiftStartTime} - {row.shiftEndTime}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Today Status",
      selector: (row) => row.todayStatus,
      sortable: true,
      width: "130px",
      cell: (row) => {
        const status = row.todayStatus || "NOT_MARKED";
        const statusColors = {
          CHECKED_IN: { bg: "#d4edda", color: "#155724", text: "Checked In" },
          CHECKED_OUT: { bg: "#cce5ff", color: "#004085", text: "Checked Out" },
          NOT_MARKED: { bg: "#f8f9fa", color: "#6c757d", text: "Not Marked" },
        };
        const style = statusColors[status] || statusColors.NOT_MARKED;
        return (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "15px",
              fontSize: "11px",
              backgroundColor: style.bg,
              color: style.color,
            }}
          >
            {style.text}
          </span>
        );
      },
    },
    {
      name: "Status",
      selector: (row) => row.active,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "15px",
            fontSize: "11px",
            backgroundColor: row.active !== false ? "#d4edda" : "#f8d7da",
            color: row.active !== false ? "#155724" : "#721c24",
          }}
        >
          {row.active !== false ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "120px",
      cell: (row) => (
        <button
          onClick={() => handleRemoveDriver(row.driverId)}
          style={{
            backgroundColor: "#dc3545",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          <i className="fa fa-trash" style={{ marginRight: "5px" }}></i>
          Remove
        </button>
      ),
      ignoreRowClick: true,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        fontSize: "13px",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "60px",
      },
    },
  };

  return (
    <div>
      <div className="form-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h5 style={{ margin: 0, color: "#333" }}>
            <i className="fa fa-users" style={{ marginRight: "10px" }}></i>
            Medical Attendance - Driver Management
          </h5>
          <button
            onClick={() => navigate("/orders/medical-attendance-config")}
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <i className="fa fa-arrow-left" style={{ marginRight: "8px" }}></i>
            Back
          </button>
        </div>

        {msg && (
          <div
            style={{
              padding: "12px 15px",
              marginBottom: "15px",
              backgroundColor: "#d4edda",
              color: "#155724",
              borderRadius: "6px",
              border: "1px solid #c3e6cb",
            }}
          >
            <i className="fa fa-check-circle" style={{ marginRight: "8px" }}></i>
            {msg}
          </div>
        )}

        {errormsg && (
          <div
            style={{
              padding: "12px 15px",
              marginBottom: "15px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "6px",
              border: "1px solid #f5c6cb",
            }}
          >
            <i className="fa fa-exclamation-circle" style={{ marginRight: "8px" }}></i>
            {errormsg}
          </div>
        )}

        {/* Partner Selection and Add Button */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ display: "flex", gap: "15px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <label style={{ fontWeight: "500", color: "#333", marginBottom: "5px", display: "block" }}>
                Select Partner
              </label>
              <select
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                }}
              >
                <option value="">-- Select a Partner --</option>
                {partners
                  .sort((a, b) => a.pname.localeCompare(b.pname))
                  .map((p) => (
                    <option key={p.pid} value={p.pid}>
                      {p.pname}
                    </option>
                  ))}
              </select>
            </div>
            <button
              onClick={() => setShowModal(true)}
              disabled={!selectedPartner}
              style={{
                backgroundColor: selectedPartner ? "#28a745" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "12px 20px",
                cursor: selectedPartner ? "pointer" : "not-allowed",
                fontSize: "14px",
                fontWeight: "500",
                height: "46px",
              }}
            >
              <i className="fa fa-user-plus" style={{ marginRight: "8px" }}></i>
              Assign Drivers
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {selectedPartner && !loading && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "15px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "15px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderLeft: "4px solid #007bff",
              }}
            >
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#007bff" }}>
                {drivers.length}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>Total Drivers</div>
            </div>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "15px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderLeft: "4px solid #28a745",
              }}
            >
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#28a745" }}>
                {drivers.filter((d) => d.todayStatus === "CHECKED_IN").length}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>Checked In Today</div>
            </div>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "15px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderLeft: "4px solid #17a2b8",
              }}
            >
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#17a2b8" }}>
                {drivers.filter((d) => d.todayStatus === "CHECKED_OUT").length}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>Checked Out</div>
            </div>
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "15px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderLeft: "4px solid #ffc107",
              }}
            >
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ffc107" }}>
                {drivers.filter((d) => !d.todayStatus || d.todayStatus === "NOT_MARKED").length}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>Not Marked</div>
            </div>
          </div>
        )}

        {/* Drivers Table */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          {!selectedPartner ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-hand-pointer-o"
                style={{ fontSize: "48px", color: "#ccc", marginBottom: "15px" }}
              ></i>
              <h5 style={{ color: "#666", marginBottom: "10px" }}>Select a Partner</h5>
              <p style={{ color: "#999" }}>
                Choose a partner from the dropdown to manage their drivers
              </p>
            </div>
          ) : loading ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: "32px", color: "#007bff" }}
              ></i>
              <p style={{ marginTop: "15px", color: "#666" }}>Loading drivers...</p>
            </div>
          ) : drivers.length === 0 ? (
            <div style={{ padding: "50px", textAlign: "center" }}>
              <i
                className="fa fa-users"
                style={{ fontSize: "48px", color: "#ffc107", marginBottom: "15px" }}
              ></i>
              <h5 style={{ color: "#666", marginBottom: "10px" }}>No Drivers Assigned</h5>
              <p style={{ color: "#999", marginBottom: "20px" }}>
                Assign drivers to medical attendance for{" "}
                <strong>{getPartnerName(parseInt(selectedPartner))}</strong>
              </p>
              <button
                onClick={() => setShowModal(true)}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "12px 24px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                <i className="fa fa-user-plus" style={{ marginRight: "8px" }}></i>
                Assign Drivers
              </button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={drivers}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          )}
        </div>
      </div>

      {/* Assign Drivers Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "25px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: "20px", color: "#333" }}>
              <i className="fa fa-user-plus" style={{ marginRight: "10px" }}></i>
              Assign Drivers to Medical Attendance
            </h5>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ fontWeight: "500", color: "#333", marginBottom: "8px", display: "block" }}>
                Select Drivers
              </label>
              <Select
                isMulti
                options={driverOptions}
                value={driverOptions.filter((d) => selectedDriverIds.includes(d.value))}
                onChange={(options) =>
                  setSelectedDriverIds(options ? options.map((o) => o.value) : [])
                }
                placeholder="Search and select drivers..."
                noOptionsMessage={() =>
                  driverOptions.length === 0
                    ? "All drivers are already assigned"
                    : "No drivers found"
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "6px",
                    minHeight: "44px",
                  }),
                }}
              />
              <small style={{ color: "#666", marginTop: "8px", display: "block" }}>
                {driverOptions.length} drivers available for assignment
              </small>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedDriverIds([]);
                }}
                style={{
                  backgroundColor: "#6c757d",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignDrivers}
                disabled={selectedDriverIds.length === 0}
                style={{
                  backgroundColor: selectedDriverIds.length > 0 ? "#28a745" : "#ccc",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  cursor: selectedDriverIds.length > 0 ? "pointer" : "not-allowed",
                }}
              >
                <i className="fa fa-check" style={{ marginRight: "6px" }}></i>
                Assign {selectedDriverIds.length > 0 ? `(${selectedDriverIds.length})` : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
